import type { ChangeEventHandler, ComponentType } from 'react'
import { useCallback, useRef, useState } from 'react'

import type { OnlyFansList } from '@api'
import { useUnit } from 'effector-react'
import debounce from 'lodash/debounce'
import { useMutation, useQueryClient } from 'react-query'
import type { ListChildComponentProps, ListOnScrollProps } from 'react-window'
import { VariableSizeList } from 'react-window'

import { IconLoading } from '@/assets/icons/icon-loading'
import SearchIcon from '@/assets/icons/icon-search.svg'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog } from '@/components/ui/dialog'
import { TextField } from '@/components/ui/text-field'
import { $creatorId } from '@/models/creators.ts'
import { FilterSortOrderOptions } from '@/modules/chatting/ui/components/filter-sort-order-options.tsx'
import { handleError } from '@/utils/error'

import { useLists } from '../../hooks/use-lists'
import EditIcon from '../../icons/edit.svg'
import { listsService } from '../../service/lists'
import type { ListToUpdate } from '../../types'
import { unescapeHtmlText } from '../chat-window/fan-info/utils'
import { ListMembersAvatars } from '../chat-window/lists/members-avatars'
import { IconButton } from '../components/icon-button'
import { useSizeObserver } from './use-size-observer'

interface Props {
  isTriggerDisabled?: boolean
}
const listToUpdateMaxSize = 10

export const EditPinnedLists = ({ isTriggerDisabled }: Props) => {
  const [listsToUpdate, setListsToUpdate] = useState<
    Record<OnlyFansList['id'], boolean>
  >({})
  const creatorId = useUnit($creatorId)
  const queryClient = useQueryClient()
  const handleClose = () => {
    setListsToUpdate({})
    setIsDialogOpen(false)
  }
  const updateListsState = useMutation(
    (listsToUpdate: Record<OnlyFansList['id'], boolean>) => {
      const requestBody: ListToUpdate[] = Object.entries(listsToUpdate).map(
        ([id, state]) => {
          return { listId: id, pinned: state }
        },
      )

      return listsService.editPinnedListState({
        creatorId: creatorId!,
        listsToUpdate: requestBody,
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('all-lists-extended')
        queryClient.invalidateQueries('pinned-lists')
        handleClose()
      },
      onError: (error) => {
        handleError(error, 'Something went wrong while updating pinned lists')
      },
    },
  )
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [setParentRef, width, height] = useSizeObserver()
  const lastVisibleIndexRef = useRef<number | null>(null)

  const {
    editableLists: lists,
    isLoading,
    canLoadMore,
    onLoadMore,
    isFetchingNextPage,
    sort,
    order,
    setSort,
    setOrder,
    setQuery,
    sortOptions,
    orderOptions,
    isSortAndOrderDisabled,
  } = useLists('all-lists-extended', {
    enabled: isDialogOpen,
  })
  const handleChangeSearch = debounce<ChangeEventHandler<HTMLInputElement>>(
    ({ target: { value } }) =>
      (!value.length || value.length > 1) && setQuery(value),
    500,
  )
  const filteredLists = lists.filter((list) => list.canPinnedToChat)

  const handleScroll = useCallback(
    ({ scrollDirection }: ListOnScrollProps) => {
      const lastItemIndex = lastVisibleIndexRef.current

      if (
        lastItemIndex === null ||
        lastItemIndex !== filteredLists.length - 1 ||
        !canLoadMore
      ) {
        return
      }

      if (scrollDirection === 'forward') {
        onLoadMore()
      }
    },
    [onLoadMore, canLoadMore, filteredLists.length],
  )

  const handleToggleCheck = useCallback(
    (list: OnlyFansList, state: boolean) => {
      setListsToUpdate((prev) => {
        const clone = { ...prev }

        if (state === list.pinnedToChat) {
          delete clone[list.id]
        } else {
          clone[list.id] = state
        }

        return clone
      })
    },
    [],
  )

  const handleSave = () => {
    updateListsState.mutateAsync(listsToUpdate)
  }

  const getItemSize = () => 83
  const listsLimitSizeReached =
    Object.keys(listsToUpdate).length >= listToUpdateMaxSize

  const renderListItem: ComponentType<ListChildComponentProps<OnlyFansList[]>> =
    useCallback(
      ({ data, index, style }) => {
        const list = data[index]

        lastVisibleIndexRef.current = index

        return (
          <div
            style={style}
            className="hover:bg-primary-50 dark:hover:bg-secondary-600 flex w-full flex-col"
          >
            <div className="group flex h-[82px] items-center gap-3 p-4">
              <Checkbox
                disabled={
                  updateListsState.isLoading ||
                  (listsLimitSizeReached && !listsToUpdate[list.id])
                }
                onCheckedChange={(value) =>
                  handleToggleCheck(
                    list,
                    typeof value === 'boolean' ? value : false,
                  )
                }
                checked={listsToUpdate[list.id] ?? list.pinnedToChat}
              />
              <ListMembersAvatars
                members={list.users}
                avatarClassName="group-hover:border-primary-50 dark:group-hover:border-secondary-600"
              />
              <div className="space-y-0.5 overflow-hidden text-ellipsis whitespace-nowrap">
                <p className="font-medium leading-[26px]">
                  {unescapeHtmlText(list.name)}
                </p>
                <p className="text-secondary-300 flex items-center gap-2 text-sm font-medium leading-[26px]">
                  {!!list.usersCount &&
                    `${list.usersCount} ${list.usersCount > 1 ? 'users' : 'user'}`}
                  {!!list.postsCount && (
                    <>
                      <span>&#x2022;</span>
                      <span>
                        {list.postsCount}{' '}
                        {list.postsCount > 1 ? 'posts' : 'post'}
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="ml-[78px] mr-4 border-b" />
          </div>
        )
      },
      [
        handleToggleCheck,
        listsLimitSizeReached,
        listsToUpdate,
        updateListsState.isLoading,
      ],
    )

  return (
    <>
      <IconButton
        icon={EditIcon}
        disabled={isTriggerDisabled}
        tooltip="Edit pinned lists"
        onClick={() => setIsDialogOpen(true)}
        className="text-title"
        rounded
      />
      <Dialog
        bigDialog
        show={isDialogOpen}
        onClose={handleClose}
        panelClassName="lg:max-w-[500px] p-6 sm:p-8"
        childrenClassName="max-w-full overflow-hidden"
      >
        <div className="space-y-6">
          <Dialog.Header>
            <Dialog.Title>Pinned List</Dialog.Title>
          </Dialog.Header>
          <div className="flex items-center gap-3">
            <TextField
              placeholder="Search"
              className="resize-none"
              prefix={
                <SearchIcon className="text-title dark:text-secondary-300" />
              }
              onChange={handleChangeSearch}
            />
            <FilterSortOrderOptions
              sort={sort}
              setSort={setSort}
              order={order}
              setOrder={setOrder}
              sortOptions={sortOptions}
              orderOptions={orderOptions}
              disabled={isSortAndOrderDisabled}
              tooltip="Sort by"
            />
          </div>
          <div
            className="no-scrollbar border-border-secondary bg-card relative h-[532px] overflow-hidden rounded-lg border py-4"
            ref={setParentRef}
          >
            {isLoading ? (
              <IconLoading className="mx-auto my-3 animate-spin" />
            ) : (
              <VariableSizeList
                onScroll={handleScroll}
                className="scrollbar-hide"
                itemData={filteredLists}
                itemSize={getItemSize}
                height={height}
                itemCount={filteredLists?.length ?? 0}
                width={width}
              >
                {renderListItem}
              </VariableSizeList>
            )}
            {isFetchingNextPage && (
              <div className="bg-card absolute bottom-0 left-0 right-0 py-3">
                <IconLoading className="mx-auto animate-spin" />
              </div>
            )}
          </div>
          <Dialog.ClosableFooter onClose={handleClose}>
            <Button
              isLoading={updateListsState.isLoading}
              onClick={handleSave}
            >
              Save
            </Button>
          </Dialog.ClosableFooter>
        </div>
      </Dialog>
    </>
  )
}
